import Box from '@mui/material/Box';
import Paper, { type PaperProps } from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ElementType, ReactNode } from 'react';

import { cssVar } from '../../theme/tokens';
import { mergeSx } from './sizes';

export interface CardProps extends Omit<PaperProps, 'title'> {
  title?: ReactNode;
  /** Heading element for the title (default h2); keep the page's heading order. */
  titleAs?: ElementType;
  description?: ReactNode;
  /** Beside the title, outside the heading: the Static or New feature badge. */
  badge?: ReactNode;
  /** Buttons on the right of the header. */
  actions?: ReactNode;
  /** Footer row (right-aligned), for example Cancel and Save. */
  footer?: ReactNode;
  /** Remove the body padding (tables and media that run edge to edge). */
  flush?: boolean;
  children?: ReactNode;
}

/** A surface for a block of content: optional header (title, description, actions) and footer. */
export function Card({
  title,
  titleAs = 'h2',
  description,
  badge,
  actions,
  footer,
  flush = false,
  children,
  sx,
  ...props
}: CardProps) {
  const hasHeader = Boolean(title || description || actions);
  return (
    <Paper
      variant="outlined"
      sx={mergeSx(
        {
          bgcolor: cssVar('surface'),
          borderColor: cssVar('border'),
          borderRadius: cssVar('radius-xl'),
          boxShadow: 'none',
          overflow: 'hidden',
        },
        sx,
      )}
      {...props}
    >
      {hasHeader ? (
        <Stack
          direction="row"
          sx={{
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 4,
            px: 6,
            pt: 5,
            pb: flush ? 5 : 0,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            {title ? (
              <Stack direction="row" sx={{ alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="cardTitle" component={titleAs}>
                  {title}
                </Typography>
                {badge}
              </Stack>
            ) : null}
            {description ? (
              <Typography variant="bodySm" sx={{ mt: 0.5, color: cssVar('text-subtle') }}>
                {description}
              </Typography>
            ) : null}
          </Box>
          {actions ? (
            <Stack direction="row" sx={{ gap: 2, flexShrink: 0 }}>
              {actions}
            </Stack>
          ) : null}
        </Stack>
      ) : null}
      {children ? <Box sx={flush ? undefined : { px: 6, py: 5 }}>{children}</Box> : null}
      {footer ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: 2,
            px: 6,
            py: 4,
            borderTop: `1px solid ${cssVar('border')}`,
            bgcolor: cssVar('surface-sunken'),
          }}
        >
          {footer}
        </Stack>
      ) : null}
    </Paper>
  );
}
